import { DepInjMixin, requires } from '@APP/mixins/dependency';
import { css, html } from 'lit';
import { customElement } from 'lit/decorators';
import { ManWatchingHorizon, ocrUploadLicenseIcon } from '../icons/icons';
import { DeviceAwareComponent } from './device-aware-component';
import { PopupService } from '@APP/services/popup-service';
import { t } from '@APP/utils/locale';
import { eventBus } from '@APP/utils/event-bus';
import { Events } from '@APP/events/app-events';

type OwnDeps = {
  popupService: PopupService;
};

@requires(PopupService.serviceName)
@customElement('lh-ocr-select')
export class LHOcrSelect extends DepInjMixin<OwnDeps>(DeviceAwareComponent) {
  static get baseStyles() {
    return [
      css`
        .center-screen {
          display: flex;
          justify-content: center;
          flex-direction: column;
        align-items: center;
        text-align: center;
        height: 50vh;
      }
      .center-screen .header-text {
        display: flex;
        padding-block: 4rem;
        color: #707070;
        font-size: 2.5rem;
        font-weight: 500;
        align-self: flex-start;
      }

      .grid-container {
        display: grid;
        grid-template-columns: 5fr 1fr 5fr;
        grid-template-rows: 20rem auto auto;
        gap: 0;
        width: 100%;
        max-width: 900px;
      }

      .column {
        display: flex;
        flex-direction: column;
        align-items: center;
        text-align: center;
      }

      .upload-card, .update-details {
        border: 2px dashed #ddd;
        border-radius: 10px;
        padding: 20px;
        display: flex;
        flex-direction: column;
        align-items: center;
        text-align: center;
        background-color: #f9f9ff;
        max-width: 300px;
      }

      .upload-card {
        border: 3px dashed #d0bfff;
        background-color:#F6F2FD;
        padding-block: 2rem;
      }

      .upload-top-row {
        display: flex;
        flex-direction: row;
        gap:2rem
      }
      
      .upload-card .icon svg {
        width:8rem;
      }

      .upload-title {
        font-size: 2.4rem;
        font-weight: bold;
        color: #26304d;
      }

      .upload-subtitle {
        font-size: 2rem;
        color: #12121399;
        margin: 0 0 5px 0;
      }

      .upload-file-types {
        font-size: 1.5rem;
        color: #12121399;
      }

      .button {
        background-color: #6a4ad8;
        color: white;
        border: none;
        padding: 6px 20px;
        border-radius: 5px;
        cursor: pointer;
        font-size: 2rem;
        margin-top: 10px;
        font-weight: bold
      }

      .button {
        background-color: var(--c-emphasize-sec);
        border: 2px solid var(--c-emphasize-sec);
        background: var(--btn-pri-bg);
        color: var(--btn-pri-text);
        border-radius: 5rem;
        min-width: 9rem;
      }
      
      .button[disabled] {
        background-color: var(--btn-disabled-color);
        color: var(--c-bg-sec);
        border: none;
      }
      .button:hover {
        background-color: var(--c-button-hover);
        border: 2px solid var(--c-button-hover);
      }

      .or-divider {
        font-size: 2.5rem;
        font-weight: bold;
        color: #555;
        align-self: center;
        grid-column: 2;
        grid-row: 2;
      }

      .icon {
        font-size: 40px;
        margin-bottom: 10px;
      }

      .icon svg{
        width: 20.6rem;
        height: 100%;
        transform: scaleX(-1) translateY(-5px);
      }

      .upload-top-row .icon svg{
        transform: translateY(-5px);
      }

      .text {
        font-size: 1.75rem;
        color: #12121399;
        margin-top: 10px;
        max-width: 250px;
        font-weight: 500;
      }

      .dashed-container {
        display: flex;
        align-items: end; /* Vertically align elements */
        justify-content: center; /* Center the content horizontally */
        gap: 1rem; /* Space between the text and the dashed lines */
        height: 100px; /* Adjust as needed */
        position: relative;
      }
     
      .dashed-container.bottom {
        align-items: start; 
      }
     
      .dashed-container.top {
        align-self: end; 
      }
      
      .dashed-line {
        width: 2px; /* Thickness of the line */
        height: 50px; /* Height of the dashed line */
        background-image: linear-gradient(to bottom, #d0bfff 50%, transparent 50%);
        background-size: 2px 10px; /* Width and spacing of the dashes */
        background-repeat: repeat-y; /* Repeat vertically */
      }

      .bottom-left {
        display: flex;
        justify-content: center;
      }
    `,
    ];
  };

  static get desktopStyles() {
    return [];
  };
  static get mobileStyles() {
    return [];
  };

  connectedCallback(): void {
    super.connectedCallback();
  }

  handleUpload() {
    this.deps.popupService.showSectionLayoutPopup('S_Boat_OCR_Details', t('T_Cancel'));
  }

  renderContent() {
    return html`
      <div class="center-screen">
      <div class="header-text">
        Please choose one of the two following options to provide your boat details:
      </div>
        <div class="grid-container">
          <!-- Top Left: Icon and Button for Search Boat -->
          <div class="column">
            <div class="icon">${ManWatchingHorizon}</div>
          </div>

          <!-- Top Center: Empty space -->
          <div class="dashed-container top">
           <div class="dashed-line"></div>
          </div>

          <!-- Top Right: Icon and Upload Card -->
          <div class="column">
            <div class="upload-card">
              <div class="upload-top-row">
                <div class="icon">${ocrUploadLicenseIcon}</div>
                <div class="upload-details">
                  <div class="upload-title">Upload Boat<br>Registration Card</div>
                  <div class="upload-subtitle">or drag and drop</div>
                </div>
              </div>
              <div class="upload-file-types">PNG, JPG, JPEG (Max size 5 MB)</div>
            </div>
          </div>

          <!-- Middle Left: Text for Search Boat -->
          <div class="column">
           <button class="button" @click=${() => eventBus().emit(Events.Type.ClickNext)}>Search Boat</button>
          </div>

          <!-- Middle Center: OR Divider -->
          <div class="or-divider">or</div>

          <!-- Middle Right: Upload Button -->
          <div class="column">
            <button class="button" @click=${this.handleUpload}>Upload</button>
          </div>

          <!-- Bottom Left: Empty space -->
          <div class="bottom-left">
           <div class="text">
              Enter your boat make, model, and year
              and we will complete the rest for you
            </div>
          </div>

          <!-- Bottom Center: Empty space -->
          <div class="dashed-container bottom">
           <div class="dashed-line"></div>
          </div>

          <!-- Bottom Right: Text for Upload -->
          <div class="column">
            <div class="text">
              Get a fast and easy quote by uploading your boat
              registration card. We’ll extract the necessary
              data, saving you time and effort.
            </div>
          </div>
        </div>
      </div>
    `;
  }
}
